import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_FORMAT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });

const createSendOfferSchema = (maxValidityDays: number) =>
  z.object({
    validityDays: z
      .string()
      .trim()
      .regex(/^\d+$/, 'Bitte eine ganze Zahl eingeben')
      .refine((value) => Number(value) >= 1 && Number(value) <= maxValidityDays, `Zwischen 1 und ${maxValidityDays} Tagen`)
  });

type SendOfferFormValues = z.infer<ReturnType<typeof createSendOfferSchema>>;

export interface SendOfferCandidate {
  mail: string;
  /** Sent before – the previous link stops working. */
  isResend: boolean;
}

export interface SendOfferDialogProps {
  /** `null` closes the dialog. */
  candidate: SendOfferCandidate | null;
  defaultValidityDays: number;
  maxValidityDays: number;
  /** Shown above the buttons, e.g. a conflict the error modal leaves out. */
  error?: string | null;
  onClose: () => void;
  /** Resolves `true` when the dialog may close. */
  onConfirm: (validityDays: number) => Promise<boolean>;
}

interface SendOfferFormProps extends Omit<SendOfferDialogProps, 'candidate'> {
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

/** Lives inside the dialog content, which unmounts while closed – so every opening starts with the default again. */
const SendOfferForm = ({ defaultValidityDays, maxValidityDays, error, onClose, onConfirm, isSubmitting, setIsSubmitting }: SendOfferFormProps) => {
  const schema = useMemo(() => createSendOfferSchema(maxValidityDays), [maxValidityDays]);
  const form = useForm<SendOfferFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { validityDays: String(defaultValidityDays) }
  });
  const validityDays = useWatch({ control: form.control, name: 'validityDays' });
  const parsedDays = schema.safeParse({ validityDays }).success ? Number(validityDays) : null;

  const handleConfirm = async (values: SendOfferFormValues) => {
    setIsSubmitting(true);
    try {
      const canClose = await onConfirm(Number(values.validityDays));
      if (canClose) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form noValidate onSubmit={form.handleSubmit(handleConfirm)} className="flex flex-col gap-5">
        <FormField
          control={form.control}
          name="validityDays"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">Link gültig (Tage) *</FormLabel>
              <FormControl>
                <Input {...field} type="number" min={1} max={maxValidityDays} step={1} className="w-32" />
              </FormControl>
              {parsedDays !== null && (
                <p className="text-[12px] text-[#888]">Gültig bis {DATE_FORMAT.format(new Date(Date.now() + parsedDays * DAY_MS))}</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {error && <p className="text-[0.8rem] font-medium text-destructive">{error}</p>}

        <DialogFooter className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Wird versendet…' : 'Versenden'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

/** Last check before the mail goes out: recipient and link validity. */
const SendOfferDialog = ({ candidate, ...formProps }: SendOfferDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && !isSubmitting && formProps.onClose()}>
      <DialogContent className="sm:max-w-md min-w-[480px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[22px] font-bold text-[#12282A]">
            {candidate?.isResend ? 'Angebot erneut versenden?' : 'Angebot versenden?'}
          </DialogTitle>
          <DialogDescription className="text-[14px] text-[#888]">
            Die Mail mit dem Link zum Angebot geht an <span className="font-bold text-[#12282A]">{candidate?.mail}</span>.
            {candidate?.isResend && ' Der bisherige Link funktioniert danach nicht mehr.'}
          </DialogDescription>
        </DialogHeader>
        <SendOfferForm {...formProps} isSubmitting={isSubmitting} setIsSubmitting={setIsSubmitting} />
      </DialogContent>
    </Dialog>
  );
};

export default SendOfferDialog;
